import type { TrafficRow } from '@netfoundry-ui/shared/model';
import {
    buildTrafficSankey,
    type SankeyChartLink,
    type SankeyChartNode,
    type TrafficSankeyData,
    type TrafficSankeyOptions,
} from '@netfoundry-ui/ui/flow-map-chart';

/**
 * Coverage layer over the shared traffic Sankey. It takes the flow-map graph plus the in-progress
 * service cards and the operator's selection, and colors each endpoint, ribbon and source/host node by how
 * much of the selected traffic the services actually cover. The same coverage test drives this and the
 * Coverage bar on the page so the diagram and the bar stay in step.
 *
 * Coverage is per flow, not just per endpoint. A flow (source -> endpoint) is covered only when some
 * intercept service governs its endpoint (address:port) AND admits its source:
 *  - a flow with a source IP is admitted when the service allows any source (empty "Allowed source
 *    addresses") or its IP matches that list (IP or CIDR);
 *  - a flow with no source IP (a Ziti identity initiator) is admitted when the service grants that flow's
 *    source identity dial access ("Accessible by").
 * Because admission is per flow, the same endpoint reached by an admitted source and an excluded one reads
 * partial, and a source node whose traffic no service admits reads uncovered. "Partial" is therefore a
 * graph state: for any node/ribbon, all its selected flows covered -> covered, some -> partial, none ->
 * uncovered. Traffic outside the selection carries no state and fades (the chart's highlight inputs).
 */

/** The fields of a service card this layer needs; the page's richer ServiceConfig satisfies it. */
export interface CoverageServiceInput {
    /** The service card's name, shown as the covering service in the detail panel. */
    name: string;
    accessMode: 'address' | 'sdk';
    addresses: string[];
    ports: string[];
    /** Host-side source lockdown (IPs/CIDRs); empty = any source allowed. */
    allowedSourceAddresses: string[];
    /** "Accessible by" tokens ('@name' / '#role'); used to admit identity initiators with no source IP. */
    dialAttributes: string[];
}

/** One selected endpoint segment from the Traffic Analysis selection. */
export interface SelectionPair {
    address: string;
    port: string;
}

export type CoverageState = 'covered' | 'partial' | 'uncovered';

/** Per-coverage-state segment tallies, for the dialog's filter pills. */
export interface CoverageCounts {
    covered: number;
    partial: number;
    uncovered: number;
    total: number;
}

export interface CoverageGraph {
    nodes: SankeyChartNode[];
    links: SankeyChartLink[];
    headers: string[];
    /** Node/link ids that carry at least one selected flow - the highlight sets for full mode and the
     *  filter for isolate mode. */
    selectedNodeIds: string[];
    selectedLinkIds: string[];
    /** Accurate, source-aware totals over the selected flows (by circuits), for the dialog's headline. */
    coveredCircuits: number;
    selectedCircuits: number;
}

// --- source-aware coverage predicate --------------------------------------

const EMPTY_IP = '(empty)'; // the fabric-circuits placeholder for an initiator with no source IP

/** Parse a dotted IPv4 address to a number, or null if it is not IPv4. Integer math (no bitwise ops). */
function toInt(ip: string): number | null {
    const parts = ip.split('.');
    if (parts.length !== 4) return null;
    let acc = 0;
    for (const part of parts) {
        const octet = Number(part);
        if (!Number.isInteger(octet) || octet < 0 || octet > 255) return null;
        acc = acc * 256 + octet;
    }
    return acc;
}

/** Whether an IPv4 address falls inside a CIDR block. Non-IPv4 inputs never match. */
function ipInCidr(ip: string, cidr: string): boolean {
    const [base, prefixStr] = cidr.split('/');
    const prefix = parseInt(prefixStr, 10);
    if (!(prefix >= 0 && prefix <= 32)) return false;
    const a = toInt(ip);
    const b = toInt(base);
    if (a === null || b === null) return false;
    // Same block when both addresses land in the same 2^(32-prefix)-sized bucket. Integer math, no masks.
    const blockSize = Math.pow(2, 32 - prefix);
    return Math.floor(a / blockSize) === Math.floor(b / blockSize);
}

/** Whether a source IP matches any allowed token (exact IP or CIDR). */
function ipMatches(ip: string, tokens: string[]): boolean {
    return tokens.some((raw) => {
        const token = raw.trim();
        if (!token) return false;
        return token.includes('/') ? ipInCidr(ip, token) : ip === token;
    });
}

/** Whether a service admits a flow's source (its IP if it has one, else its dialing identity). */
function sourceAdmitted(sourceIp: string, identityName: string, s: CoverageServiceInput): boolean {
    const ip = (sourceIp ?? '').trim();
    if (ip && ip !== EMPTY_IP) {
        const allowed = s.allowedSourceAddresses ?? [];
        return allowed.length === 0 || ipMatches(ip, allowed);
    }
    // Identity initiator (no source IP): admitted only if the service grants it dial access by name.
    const name = (identityName ?? '').trim();
    return !!name && (s.dialAttributes ?? []).includes(`@${name}`);
}

/**
 * Whether an observed endpoint segment (address:port) is governed by some intercept service, ignoring the
 * source side. This is the coarse "segment is placed in a service" test the page's Coverage bar shows;
 * the diagram uses the per-flow {@link isFlowCovered} instead. SDK services never govern an address:port.
 */
export function isSegmentCovered(address: string, port: string, services: CoverageServiceInput[]): boolean {
    return services.some(
        (s) => s.accessMode === 'address' && s.addresses.includes(address) && s.ports.includes(port)
    );
}

/** Whether a single flow is covered: some intercept service governs its endpoint and admits its source. */
export function isFlowCovered(
    sourceIp: string,
    identityName: string,
    address: string,
    port: string,
    services: CoverageServiceInput[]
): boolean {
    return services.some(
        (s) =>
            s.accessMode === 'address' &&
            s.addresses.includes(address) &&
            s.ports.includes(port) &&
            sourceAdmitted(sourceIp, identityName, s)
    );
}

// --- graph building --------------------------------------------------------

function addrOf(flow: TrafficRow): string {
    return (flow.terminator_remote_address ?? '').trim();
}
function portOf(flow: TrafficRow): string {
    return (flow.terminator_remote_port ?? '').trim();
}

// --- click-to-explain (the detail side panel) ------------------------------

/** Which dimension the detail panel's rows break the flows into. */
export type CoverageRowBy = 'source' | 'endpoint';

/** One row of the detail panel's coverage breakdown. */
export interface CoverageRow {
    /** A source (IP, or `@identity` for a no-IP initiator) when rowsBy='source'; an `address:port`
     *  endpoint when rowsBy='endpoint'. Shown verbatim. */
    label: string;
    circuits: number;
    state: CoverageState;
    /** Source rows only: whether a governing service admits this source (the criteria-matrix check). */
    allowed?: boolean;
    /** Endpoint rows only: the covering service's name, or '' when no service governs the endpoint. */
    service?: string;
    /** The flows this row aggregates, so hovering it can highlight its path in the coverage flow-map. */
    flows?: TrafficRow[];
}

/** A tree node's role: source side (identity / address / dial-only), or endpoint side (host / endpoint). */
export type CoverageNodeKind = 'identity' | 'address' | 'noip' | 'host' | 'endpoint';

/** One node of the source breakdown tree: a source identity (parent) over its source addresses (leaves),
 *  or a dial-only identity (a leaf of its own). Carries the coverage state and the include state, both
 *  rolled up from the leaves, plus the admission targets to edit when its include checkbox is toggled. */
export interface CoverageTreeNode {
    label: string;
    kind: CoverageNodeKind;
    circuits: number;
    state: CoverageState;
    /** Whether every source under this node is admitted by the governing service (the checkbox checked state). */
    included: boolean;
    /** A parent only some of whose sources are included (the checkbox shows a dash). */
    indeterminate: boolean;
    /** The admission targets under this node: IPs (for addresses) and `@identity` tokens (for dial sources). */
    sources: string[];
    /** The flows under this node, so hovering it highlights its path in the coverage flow-map. */
    flows: TrafficRow[];
    children?: CoverageTreeNode[];
}

/** The structured coverage readout for a clicked node/ribbon - no prose; the UI renders it as tiles,
 *  a meter and a criteria matrix. */
export interface CoverageExplain {
    rowsBy: CoverageRowBy;
    /** Headline meter: covered vs selected traffic, by circuits. */
    coveredCircuits: number;
    selectedCircuits: number;
    /** Orthogonal tiles: distinct sources, and how many have no covered traffic. */
    sourceCount: number;
    excludedSourceCount: number;
    /** Distinct governing-service names across this element's selected flows (the "Service" chip). */
    services: string[];
    /** Whether any service governs this element's endpoint(s) - i.e. there is a service to include/exclude
     *  sources from. Independent of the service having a name (unnamed cards still govern). */
    governed: boolean;
    /** Whether any address-mode service exists at all (one an endpoint can be added back to). Stays true
     *  after excluding an endpoint, so the include checkbox never disappears and the exclusion is reversible. */
    editable: boolean;
    rows: CoverageRow[];
    /** Source breakdowns (endpoint click): the Source identity -> Source address tree. Absent otherwise. */
    tree?: CoverageTreeNode[];
}

/** A flow's source key: its IP, or `@identity` for a no-IP (identity) initiator. */
function sourceKey(flow: TrafficRow): string {
    const ip = (flow.initiator_remote_ip ?? '').trim();
    if (ip && ip !== EMPTY_IP) return ip;
    const id = (flow.identity_name ?? '').trim();
    return id ? `@${id}` : '(no source)';
}

/** Names of the services that govern an endpoint (address:port), ignoring the source side. */
function governingServiceNames(address: string, port: string, services: CoverageServiceInput[]): string[] {
    return services
        .filter((s) => s.accessMode === 'address' && s.addresses.includes(address) && s.ports.includes(port))
        .map((s) => (s.name ?? '').trim())
        .filter(Boolean);
}

function pushRow<K>(map: Map<K, TrafficRow[]>, key: K, flow: TrafficRow): void {
    const bucket = map.get(key);
    if (bucket) bucket.push(flow);
    else map.set(key, [flow]);
}

/**
 * Build the structured coverage readout for a clicked element's flows. `rowsBy` is chosen by the caller
 * from the clicked node's column: an endpoint breaks down by source (the varying criterion is source
 * admission), everything else by endpoint. Only the selected flows count; the result carries the meter
 * figures, the orthogonal tile counts, the governing services, and one row per group.
 */
export function explainFlows(
    flows: TrafficRow[],
    selectionPairs: SelectionPair[],
    services: CoverageServiceInput[],
    rowsBy: CoverageRowBy
): CoverageExplain {
    const selected = new Set(selectionPairs.map((p) => `${p.address}:${p.port}`));
    const sel = flows.filter((f) => selected.has(`${addrOf(f)}:${portOf(f)}`));

    let coveredCircuits = 0;
    let selectedCircuits = 0;
    let governed = false;
    const groups = new Map<string, TrafficRow[]>();
    const serviceNames = new Set<string>();
    // distinct source -> has any covered flow (for the source counts, independent of rowsBy)
    const sourceCovered = new Map<string, boolean>();

    for (const flow of sel) {
        const circuits = flow.circuits || 0;
        const covered = isFlowCovered(flow.initiator_remote_ip, flow.identity_name, addrOf(flow), portOf(flow), services);
        selectedCircuits += circuits;
        if (covered) coveredCircuits += circuits;
        if (!governed && isGoverned(addrOf(flow), portOf(flow), services)) governed = true;
        governingServiceNames(addrOf(flow), portOf(flow), services).forEach((n) => serviceNames.add(n));
        const src = sourceKey(flow);
        sourceCovered.set(src, (sourceCovered.get(src) ?? false) || covered);
        pushRow(groups, rowsBy === 'source' ? src : `${addrOf(flow)}:${portOf(flow)}`, flow);
    }

    const rows: CoverageRow[] = [];
    groups.forEach((groupFlows, label) => {
        let circuits = 0;
        let cov = 0;
        let service = '';
        for (const flow of groupFlows) {
            const c = flow.circuits || 0;
            circuits += c;
            if (isFlowCovered(flow.initiator_remote_ip, flow.identity_name, addrOf(flow), portOf(flow), services)) cov += c;
            if (!service) service = governingServiceNames(addrOf(flow), portOf(flow), services)[0] ?? '';
        }
        const state: CoverageState = cov === circuits ? 'covered' : cov === 0 ? 'uncovered' : 'partial';
        const row: CoverageRow = { label, circuits, state, flows: groupFlows };
        if (rowsBy === 'source') row.allowed = state === 'covered';
        else row.service = service;
        rows.push(row);
    });
    rows.sort((a, b) => b.circuits - a.circuits);

    let excludedSourceCount = 0;
    sourceCovered.forEach((hasCovered) => {
        if (!hasCovered) excludedSourceCount += 1;
    });

    return {
        rowsBy,
        coveredCircuits,
        selectedCircuits,
        sourceCount: sourceCovered.size,
        excludedSourceCount,
        services: [...serviceNames],
        governed,
        editable: services.some((s) => s.accessMode === 'address'),
        rows,
        tree: rowsBy === 'source' ? sourceTree(sel, services) : endpointTree(sel, services),
    };
}

/** Build the Source identity -> Source address tree for an endpoint's selected flows. Each identity is a
 *  parent over its source-address leaves; an identity that only dials (no source IP) collapses to a single
 *  leaf. Coverage state and include state roll up from the leaves. Busiest first at each level. */
function sourceTree(sel: TrafficRow[], services: CoverageServiceInput[]): CoverageTreeNode[] {
    const byIdentity = new Map<string, TrafficRow[]>();
    for (const flow of sel) {
        const identity = (flow.identity_name ?? '').trim() || '(no identity)';
        pushRow(byIdentity, identity, flow);
    }

    const nodes: CoverageTreeNode[] = [];
    byIdentity.forEach((identityFlows, identity) => {
        const byIp = new Map<string, TrafficRow[]>();
        const dialFlows: TrafficRow[] = [];
        for (const flow of identityFlows) {
            const ip = (flow.initiator_remote_ip ?? '').trim();
            if (ip && ip !== EMPTY_IP) pushRow(byIp, ip, flow);
            else dialFlows.push(flow);
        }

        const children: CoverageTreeNode[] = [];
        byIp.forEach((ipFlows, ip) => children.push(leafNode(ip, 'address', ip, ipFlows, services)));
        if (dialFlows.length) children.push(leafNode('(no source address)', 'noip', `@${identity}`, dialFlows, services));

        if (children.length === 1 && children[0].kind === 'noip') {
            // Dial-only identity: show it as a single leaf rather than a parent with one child.
            nodes.push({ ...children[0], label: `@${identity}` });
        } else {
            nodes.push(parentNode(identity, 'identity', children));
        }
    });

    sortByCircuits(nodes);
    nodes.forEach((n) => n.children && sortByCircuits(n.children));
    return nodes;
}

/** Build the Host identity -> Host address (endpoint) tree for a source-side node's reached flows. Each
 *  host identity is a parent over the endpoints it serves; coverage state rolls up. The leaf's `sources`
 *  carries its `address:port` so the include checkbox can target the service's addresses/ports. */
function endpointTree(sel: TrafficRow[], services: CoverageServiceInput[]): CoverageTreeNode[] {
    const byHost = new Map<string, TrafficRow[]>();
    for (const flow of sel) {
        const host = (flow.host_entity_name ?? '').trim() || '(no host identity)';
        pushRow(byHost, host, flow);
    }

    const nodes: CoverageTreeNode[] = [];
    byHost.forEach((hostFlows, host) => {
        const byEndpoint = new Map<string, TrafficRow[]>();
        for (const flow of hostFlows) pushRow(byEndpoint, `${addrOf(flow)}:${portOf(flow)}`, flow);
        const children: CoverageTreeNode[] = [];
        byEndpoint.forEach((epFlows, endpoint) => children.push(leafNode(endpoint, 'endpoint', endpoint, epFlows, services)));
        nodes.push(parentNode(host, 'host', children));
    });

    sortByCircuits(nodes);
    nodes.forEach((n) => n.children && sortByCircuits(n.children));
    return nodes;
}

function leafNode(
    label: string,
    kind: CoverageNodeKind,
    source: string,
    flows: TrafficRow[],
    services: CoverageServiceInput[]
): CoverageTreeNode {
    let circuits = 0;
    let covered = 0;
    for (const flow of flows) {
        const c = flow.circuits || 0;
        circuits += c;
        if (isFlowCovered(flow.initiator_remote_ip, flow.identity_name, addrOf(flow), portOf(flow), services)) covered += c;
    }
    const state: CoverageState = covered === circuits ? 'covered' : covered === 0 ? 'uncovered' : 'partial';
    return {
        label,
        kind,
        circuits,
        state,
        included: state === 'covered',
        indeterminate: false,
        sources: [source],
        flows,
    };
}

function parentNode(label: string, kind: CoverageNodeKind, children: CoverageTreeNode[]): CoverageTreeNode {
    const circuits = children.reduce((t, c) => t + c.circuits, 0);
    const allCovered = children.every((c) => c.state === 'covered');
    const noneCovered = children.every((c) => c.state === 'uncovered');
    const state: CoverageState = allCovered ? 'covered' : noneCovered ? 'uncovered' : 'partial';
    const included = children.every((c) => c.included);
    const anyIncluded = children.some((c) => c.included || c.indeterminate);
    return {
        label,
        kind,
        circuits,
        state,
        included,
        indeterminate: !included && anyIncluded,
        sources: [...new Set(children.flatMap((c) => c.sources))],
        flows: children.flatMap((c) => c.flows),
        children,
    };
}

function sortByCircuits(nodes: CoverageTreeNode[]): void {
    nodes.sort((a, b) => b.circuits - a.circuits);
}

/** Whether any address-mode service targets this exact endpoint (regardless of source admission or name). */
function isGoverned(address: string, port: string, services: CoverageServiceInput[]): boolean {
    return services.some((s) => s.accessMode === 'address' && s.addresses.includes(address) && s.ports.includes(port));
}

/** Whether a list admits an IP source (an empty list admits every source; otherwise it must match). */
function admittedByList(ip: string, allowed: string[]): boolean {
    return allowed.length === 0 || ipMatches(ip, allowed);
}

/** The distinct source IPs seen among a set of flows (identity initiators, with no IP, are left out). */
function distinctSourceIps(flows: TrafficRow[]): string[] {
    const set = new Set<string>();
    for (const f of flows) {
        const ip = (f.initiator_remote_ip ?? '').trim();
        if (ip && ip !== EMPTY_IP) set.add(ip);
    }
    return [...set];
}

/**
 * Include or exclude a source from the service(s) governing an endpoint, editing `services` in place.
 * `endpointFlows` are the clicked endpoint's flows (they share its address:port); `source` is the breakdown
 * row label - an IP, or `@identity` for a no-IP initiator. Returns whether anything changed.
 *
 * - Include an identity: add `@name` to each governing service's dial attributes. Exclude: remove it.
 * - Include an IP: add it to each governing service's allowed source addresses (a service that already
 *   admits every source - an empty list - needs no change).
 * - Exclude an IP: drop it when it is an explicit entry; when it is admitted by an empty list (all sources)
 *   or by a CIDR, replace the list with the other observed admitted IPs so the exclusion takes effect
 *   (this narrows an "allow any" service to the sources actually seen here, minus the excluded one).
 */
export function setSourceIncluded(
    endpointFlows: TrafficRow[],
    source: string,
    include: boolean,
    services: CoverageServiceInput[],
    contextFlows: TrafficRow[] = endpointFlows
): boolean {
    if (!endpointFlows.length) return false;
    const address = addrOf(endpointFlows[0]);
    const port = portOf(endpointFlows[0]);
    const governing = services.filter(
        (s) => s.accessMode === 'address' && s.addresses.includes(address) && s.ports.includes(port)
    );
    if (!governing.length) return false;

    const isIdentity = source.startsWith('@');
    const identityToken = source; // already in `@name` form
    const ip = isIdentity ? '' : source;
    let changed = false;

    for (const s of governing) {
        if (isIdentity) {
            const has = s.dialAttributes.includes(identityToken);
            if (include && !has) {
                s.dialAttributes = [...s.dialAttributes, identityToken];
                changed = true;
            } else if (!include && has) {
                s.dialAttributes = s.dialAttributes.filter((t) => t !== identityToken);
                changed = true;
            }
            continue;
        }
        const allowed = s.allowedSourceAddresses ?? [];
        if (include) {
            // An empty list already admits every source; otherwise add the IP if it is not listed.
            if (allowed.length > 0 && !allowed.includes(ip)) {
                s.allowedSourceAddresses = [...allowed, ip];
                changed = true;
            }
        } else if (allowed.includes(ip)) {
            s.allowedSourceAddresses = allowed.filter((a) => a !== ip);
            changed = true;
        } else if (admittedByList(ip, allowed)) {
            // Admitted via an empty list or a CIDR: materialize an explicit allowlist of every OTHER admitted
            // source (from the whole graph, not just the clicked segment), minus this one, so excluding one
            // source leaves all the others covered rather than dropping everyone not in the segment.
            s.allowedSourceAddresses = distinctSourceIps(contextFlows).filter(
                (other) => other !== ip && admittedByList(other, allowed)
            );
            changed = true;
        }
    }
    return changed;
}

/** Distinct endpoints (address + port) seen among a set of flows. */
function distinctEndpoints(flows: TrafficRow[]): { address: string; port: string }[] {
    const seen = new Set<string>();
    const out: { address: string; port: string }[] = [];
    for (const flow of flows) {
        const address = addrOf(flow);
        const port = portOf(flow);
        const key = `${address}:${port}`;
        if (!seen.has(key)) {
            seen.add(key);
            out.push({ address, port });
        }
    }
    return out;
}

/**
 * Include or exclude an endpoint (address:port) from the services, editing `services` in place. Returns
 * whether anything changed. Because a service covers the CROSS PRODUCT of its addresses x ports, this is
 * best-effort at the single-endpoint level:
 * - Include: ensure the endpoint's address and port are on a target service (one already covering a related
 *   address or port, else the first address-mode service). May incidentally cover other address/port combos.
 * - Exclude: from each service that covers the endpoint, drop its address and/or port only when no OTHER
 *   observed covered endpoint still needs that address (with one of the service's ports) or that port (with
 *   one of its addresses). When BOTH are still needed elsewhere, the endpoint cannot be carved out without
 *   splitting the service, so it is left covered.
 * `contextFlows` are the flows whose endpoints define "still needed" (pass the graph's full flows).
 */
export function setEndpointIncluded(
    address: string,
    port: string,
    include: boolean,
    services: CoverageServiceInput[],
    contextFlows: TrafficRow[]
): boolean {
    if (!address) return false;
    if (include) {
        const target =
            services.find((s) => s.accessMode === 'address' && (s.addresses.includes(address) || s.ports.includes(port))) ??
            services.find((s) => s.accessMode === 'address');
        if (!target) return false;
        let changed = false;
        if (!target.addresses.includes(address)) {
            target.addresses = [...target.addresses, address];
            changed = true;
        }
        if (port && !target.ports.includes(port)) {
            target.ports = [...target.ports, port];
            changed = true;
        }
        return changed;
    }
    // Exclude: keep every other observed endpoint covered.
    const others = distinctEndpoints(contextFlows).filter((e) => !(e.address === address && e.port === port));
    let changed = false;
    for (const s of services) {
        if (s.accessMode !== 'address' || !s.addresses.includes(address) || !s.ports.includes(port)) continue;
        const addressNeeded = others.some((e) => e.address === address && s.ports.includes(e.port));
        const portNeeded = others.some((e) => e.port === port && s.addresses.includes(e.address));
        if (!addressNeeded) {
            s.addresses = s.addresses.filter((a) => a !== address);
            changed = true;
        }
        if (!portNeeded && port) {
            s.ports = s.ports.filter((p) => p !== port);
            changed = true;
        }
        // If both are still needed elsewhere, the endpoint stays covered (cannot split the cross product).
    }
    return changed;
}

/**
 * Coverage classification for a bundle of flows (a node's or a ribbon's). Looks only at the flows whose
 * endpoint is in the selection; if none are selected the bundle is not part of this coverage question (it
 * fades). Otherwise all selected flows covered -> covered, some -> partial, none -> uncovered.
 */
function classify(
    flows: TrafficRow[],
    selected: Set<string>,
    services: CoverageServiceInput[]
): { selected: boolean; state?: CoverageState } {
    let sel = 0;
    let cov = 0;
    for (const flow of flows) {
        const addr = addrOf(flow);
        const port = portOf(flow);
        if (!selected.has(`${addr}:${port}`)) continue;
        sel += 1;
        if (isFlowCovered(flow.initiator_remote_ip, flow.identity_name, addr, port, services)) cov += 1;
    }
    if (sel === 0) return { selected: false };
    if (cov === sel) return { selected: true, state: 'covered' };
    if (cov === 0) return { selected: true, state: 'uncovered' };
    return { selected: true, state: 'partial' };
}

/** How a mixed ribbon is drawn: one amber "partial" band, or split green/neutral by circuit volume. */
export type RibbonStyle = 'blended' | 'proportional';

/** The covered / uncovered / not-selected circuit volume a ribbon carries. */
function linkBands(
    flows: TrafficRow[],
    selected: Set<string>,
    services: CoverageServiceInput[]
): { covered: number; uncovered: number; context: number } {
    let covered = 0;
    let uncovered = 0;
    let context = 0;
    for (const flow of flows) {
        const circuits = flow.circuits || 0;
        const addr = addrOf(flow);
        const port = portOf(flow);
        if (!selected.has(`${addr}:${port}`)) {
            context += circuits;
            continue;
        }
        if (isFlowCovered(flow.initiator_remote_ip, flow.identity_name, addr, port, services)) covered += circuits;
        else uncovered += circuits;
    }
    return { covered, uncovered, context };
}

/**
 * Build the coverage-colored graph from a traffic Sankey, the selection, and the service cards. Endpoint,
 * source and host nodes plus the ribbons all get a coverage state; catch-all buckets keep their muted
 * styling. In 'proportional' ribbon style a ribbon carrying a mix of covered and uncovered traffic is
 * split into a green band and a neutral band sized by circuit volume (and a faded band for any
 * not-selected context), instead of a single amber "partial" band.
 */
export function buildCoverageGraph(
    data: TrafficSankeyData,
    selectionPairs: SelectionPair[],
    services: CoverageServiceInput[],
    ribbonStyle: RibbonStyle = 'blended'
): CoverageGraph {
    const selected = new Set(selectionPairs.map((p) => `${p.address}:${p.port}`));

    const selectedNodeIds: string[] = [];
    const nodes = data.nodes.map((node) => {
        const c = classify(data.flowsByNode.get(node.id) ?? [], selected, services);
        if (!c.selected) return node;
        selectedNodeIds.push(node.id);
        // Keep catch-all buckets muted; color every real node on the covered path.
        return node.kind === 'catch-all' ? node : { ...node, kind: `cov-${c.state}` };
    });

    const selectedLinkIds: string[] = [];
    const links: SankeyChartLink[] = [];
    data.links.forEach((link) => {
        const flows = data.flowsByLink.get(link.id) ?? [];
        if (ribbonStyle === 'proportional') {
            // Split by circuit volume: a green covered band, a neutral uncovered band, and a faded
            // not-selected band. The three sum to the ribbon's original weight, so node heights are
            // unchanged. Sub-links share source/target, so d3 stacks them adjacently (covered first).
            const b = linkBands(flows, selected, services);
            if (b.covered > 0) {
                links.push({ ...link, id: `${link.id}#cov`, weight: b.covered, state: 'covered' });
                selectedLinkIds.push(`${link.id}#cov`);
            }
            if (b.uncovered > 0) {
                links.push({ ...link, id: `${link.id}#unc`, weight: b.uncovered, state: 'uncovered' });
                selectedLinkIds.push(`${link.id}#unc`);
            }
            if (b.context > 0) {
                links.push({ ...link, id: `${link.id}#ctx`, weight: b.context });
            }
            if (b.covered === 0 && b.uncovered === 0 && b.context === 0) links.push(link);
            return;
        }
        const c = classify(flows, selected, services);
        if (!c.selected) {
            links.push(link);
            return;
        }
        selectedLinkIds.push(link.id);
        links.push({ ...link, state: c.state });
    });

    // Accurate totals over the selected flows, by circuits, for the dialog headline. Each flow appears
    // once under its endpoint-stage node ("3:<endpoint>"), so sum over that stage to count every flow once.
    let coveredCircuits = 0;
    let selectedCircuits = 0;
    data.flowsByNode.forEach((flows, nodeId) => {
        if (!nodeId.startsWith('3:')) return;
        for (const flow of flows) {
            const addr = addrOf(flow);
            const port = portOf(flow);
            if (!selected.has(`${addr}:${port}`)) continue;
            const circuits = flow.circuits || 0;
            selectedCircuits += circuits;
            if (isFlowCovered(flow.initiator_remote_ip, flow.identity_name, addr, port, services)) {
                coveredCircuits += circuits;
            }
        }
    });

    return { nodes, links, headers: data.headers, selectedNodeIds, selectedLinkIds, coveredCircuits, selectedCircuits };
}

/**
 * The coverage state of each selected segment (address:port), source-aware. A segment with observed
 * flows reads covered/partial/uncovered by how many of its flows are covered; a selected segment with no
 * flows in the window falls back to the coarse endpoint-only test (covered or uncovered).
 */
export function segmentStates(
    flows: TrafficRow[],
    selectionPairs: SelectionPair[],
    services: CoverageServiceInput[]
): Map<string, CoverageState> {
    const selected = new Set(selectionPairs.map((p) => `${p.address}:${p.port}`));
    const tally = new Map<string, { sel: number; cov: number }>();
    for (const flow of flows) {
        const addr = addrOf(flow);
        const port = portOf(flow);
        const key = `${addr}:${port}`;
        if (!selected.has(key)) continue;
        const entry = tally.get(key) ?? { sel: 0, cov: 0 };
        entry.sel += 1;
        if (isFlowCovered(flow.initiator_remote_ip, flow.identity_name, addr, port, services)) entry.cov += 1;
        tally.set(key, entry);
    }
    const states = new Map<string, CoverageState>();
    for (const pair of selectionPairs) {
        const key = `${pair.address}:${pair.port}`;
        const entry = tally.get(key);
        if (!entry || entry.sel === 0) {
            states.set(key, isSegmentCovered(pair.address, pair.port, services) ? 'covered' : 'uncovered');
            continue;
        }
        states.set(key, entry.cov === entry.sel ? 'covered' : entry.cov === 0 ? 'uncovered' : 'partial');
    }
    return states;
}

/** Tally segment states into per-state counts for the filter pills. */
export function coverageCounts(states: Map<string, CoverageState>): CoverageCounts {
    let covered = 0;
    let partial = 0;
    let uncovered = 0;
    states.forEach((state) => {
        if (state === 'covered') covered += 1;
        else if (state === 'partial') partial += 1;
        else uncovered += 1;
    });
    return { covered, partial, uncovered, total: states.size };
}

/** The address:port key for a flow, matching the keys used by {@link segmentStates}. */
export function flowPairKey(flow: TrafficRow): string {
    return `${addrOf(flow)}:${portOf(flow)}`;
}

/** Narrow a coverage graph to just the selected path (isolate / "Selected only" mode). */
export function isolateSelected(graph: CoverageGraph): CoverageGraph {
    const nodeSet = new Set(graph.selectedNodeIds);
    const linkSet = new Set(graph.selectedLinkIds);
    return {
        ...graph,
        nodes: graph.nodes.filter((n) => nodeSet.has(n.id)),
        links: graph.links.filter((l) => linkSet.has(l.id)),
    };
}

/**
 * Synthesize a minimal set of flows from the condensed selection, for when the originating time window is
 * missing or the re-query fails. It fans each selected source (IP, or a no-IP identity-initiator stand-in)
 * and source identity through to each endpoint and on to the host identities, with unit weight - enough to
 * draw a from-selection graph colored by coverage (including the source-admission rules), but with no real
 * magnitudes and no "not selected" context. Bounded so a large selection cannot explode.
 */
export function buildSelectionFlows(
    pairs: SelectionPair[],
    srcIdentities: string[],
    srcIps: string[],
    hostIdentities: string[],
    maxFlows = 800
): TrafficRow[] {
    const identities = srcIdentities.length ? srcIdentities : [''];
    const hosts = hostIdentities.length ? hostIdentities : [''];
    const sources = srcIps.length ? srcIps : [EMPTY_IP];
    const flows: TrafficRow[] = [];
    for (const pair of pairs) {
        if (!pair.address || !pair.port) continue;
        for (const identity of identities) {
            for (const host of hosts) {
                for (const ip of sources) {
                    if (flows.length >= maxFlows) return flows;
                    /* eslint-disable @typescript-eslint/naming-convention */
                    flows.push({
                        terminator_remote_address: pair.address,
                        terminator_remote_port: pair.port,
                        identity_name: identity,
                        host_entity_name: host,
                        initiator_remote_ip: ip,
                        service_name: '',
                        circuits: 1,
                        id: '',
                    } as TrafficRow);
                    /* eslint-enable @typescript-eslint/naming-convention */
                }
            }
        }
    }
    return flows;
}

/** Build the full coverage graph straight from flows - the shared Sankey plus the coverage layer. */
export function coverageGraphFromFlows(
    flows: TrafficRow[],
    selectionPairs: SelectionPair[],
    services: CoverageServiceInput[],
    ribbonStyle: RibbonStyle = 'blended',
    options: TrafficSankeyOptions = {}
): CoverageGraph {
    const data: TrafficSankeyData = buildTrafficSankey(flows, options);
    return buildCoverageGraph(data, selectionPairs, services, ribbonStyle);
}
